import constants from "constants/constants";
import moment from "moment";
import { checkIsStringEmpty } from "features/Common/modules/DataTypes/Strings.utils";
import { CURRENCY_SYMBOL_MAP } from "features/Common/modules/Currency/constants/Currency.constants";

export const SHOW_TOTAL_VISITORS = true; // TODO: Change this to hide/show total visitors data in CT

/**
 * Smoothly scrolls the page to the element with the specified ID.
 *
 * @param {string} elementId - The ID of the element to scroll to.
 * @param {object} options - explicit scroll behaviour options (subset of scrollIntoViewOptions)
 This function scrolls to the target element using the `scrollIntoView` method with smooth scrolling behavior.
 *
 */
export const onScrollToDiv = ({ elementId, ...restParams }) => {
  setTimeout(() => {
    const divElement = document.getElementById(elementId);
    divElement &&
      divElement.scrollIntoView({ behavior: "smooth", ...restParams });
  }, 10);
};

// Helper function to trigger resize and scroll element into view
export const triggerResizeAndScroll = ({ id, throttleTime }) => {
  let resizeTimeout;
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    window.dispatchEvent(new Event("resize"));
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, throttleTime);
};

export const is_empty = (str) => {
  return (
    !str ||
    0 === str.length ||
    (str.constructor === Object && Object.keys(str).length === 0) ||
    (str.constructor === String && str.trim().length === 0)
  );
};

/**
 * to pause process for given duration, then the following code should execute\
 * ref: https://stackoverflow.com/a/33292942
 * @param {*} duration time in ms
 * @returns promise to await
 */
export const pauseProcess = (duration) => {
  return new Promise((resolve) => setTimeout(resolve, duration));
};

/**
 * calculates the percentage of a numerator with respect to a denominator
 * @param {*} numerator value whose percentage needs to be calculated
 * @param {*} denominator total value
 * @returns formatted percentage string with 2 decimal places or "N/A" when the denominator is unavailable
 */
export const getPercentage = (numerator, denominator) => {
  if (numerator == null || denominator == null || denominator === 0) {
    return "N/A";
  }

  return `${((numerator / denominator) * 100).toFixed(2)}%`;
};

/**
 * Exports a list of recipients to a CSV file.
 *
 * @param {Array} data - The array of recipient objects to export.
 * @param {string} fileName - The filename for the downloaded CSV file.
 * @param {boolean|object} [options=false] - Whether it is WhatsApp campaign or options object.
 */
export const exportToCSV = (data, fileName, options = false) => {
  const isWhatsApp =
    typeof options === "object" ? !!options.isWhatsApp : !!options;

  const headers = isWhatsApp
    ? constants.export_recipient_headers_whatsapp
    : constants.export_recipient_headers_email;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return moment(dateString).format(
      constants.internationalInputDateTimeFormatWithSaparation
    );
  };

  const csvRows = [headers.join(",")];

  data.forEach((item) => {
    const row = isWhatsApp
      ? [
          `"${(item.name || "").replace(/"/g, '""')}"`,
          `"${(item.phone_number || item.phone || "").replace(/"/g, '""')}"`,
          `"${formatDate(item.attempted_at)}"`,
          `"${formatDate(item.sent_at)}"`,
        ]
      : [
          `"${(item.name || "").replace(/"/g, '""')}"`,
          `"${(item.email || "").replace(/"/g, '""')}"`,
          `"${formatDate(item.attempted_at)}"`,
          `"${formatDate(item.sent_at)}"`,
        ];
    csvRows.push(row.join(","));
  });

  const csvContent = "\uFEFF" + csvRows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Formats a numeric value or numeric string into whole numbers with the creator's currency symbol and commas.
 * (e.g. 45900 -> "₹45,900" for INR, "$45,900" for USD)
 * @param {number|string} value
 * @param {string} [currencySymbol]
 * @param {number} [fractionDigits=0] decimals to keep (0 = whole numbers)
 * @returns {string} Formatted currency string
 */
export const formatRevenue = (value, currencySymbol = null, fractionDigits = 0) => {
  let symbol = currencySymbol;
  if (!symbol) {
    try {
      const { getUserCurrencySymbol } = require("utils/AuthUtil");
      symbol = getUserCurrencySymbol();
    } catch (e) {}
  }
  if (!symbol) {
    try {
      const authStr = localStorage.getItem("auth");
      if (authStr) {
        const auth = JSON.parse(authStr);
        if (auth?.currency_symbol) {
          symbol = auth.currency_symbol;
        } else if (auth?.creator_currency) {
          symbol = CURRENCY_SYMBOL_MAP[auth.creator_currency];
        }
      }
    } catch (e) {}
  }
  if (!symbol) {
    symbol = CURRENCY_SYMBOL_MAP.INR;
  }

  if (!checkIsStringEmpty(value)) {
    return `${symbol}0`;
  }

  const numeric = Number(value);
  const num =
    fractionDigits > 0 ? numeric : Math.round(numeric);

  if (isNaN(num)) {
    if (
      typeof value === "string" &&
      Object.values(CURRENCY_SYMBOL_MAP).some((s) => value.trim().startsWith(s))
    ) {
      return value;
    }
    return `${symbol}${value}`;
  }

  const locale = symbol === CURRENCY_SYMBOL_MAP.INR ? "en-IN" : "en-US";
  const formatted = num.toLocaleString(locale, {
    minimumFractionDigits: Math.max(0, fractionDigits),
    maximumFractionDigits: Math.max(0, fractionDigits),
  });

  return `${symbol}${formatted}`;
};
